import { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Space, Tag, Card, Typography, Statistic, Row, Col, Tooltip, Popconfirm, Drawer, Descriptions, CopyOutlined, Progress, Timeline, Badge, Alert } from 'antd';
import { ReloadOutlined, EyeOutlined, DeleteOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, FileTextOutlined, SafetyOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { t } from '../utils/i18n';

const { Title, Text } = Typography;

const Batches = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await adminAPI.getAdminBatches({ page, page_size: pageSize });
      if (response.code === 0 || response.data) {
        const data = response.data || {};
        const batchList = data.batches || response.batches || [];
        setBatches(batchList);
        setPagination({
          current: data.page || page,
          pageSize: data.pageSize || pageSize,
          total: data.total || batchList.length,
        });
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error('Failed to fetch batches:', error);
      message.error(t('fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    fetchBatches(paginationConfig.current, paginationConfig.pageSize);
  };

  const showDetails = async (batch) => {
    try {
      const response = await adminAPI.getAdminBatch(batch.id);
      if (response.data?.batch) {
        setCurrentBatch(response.data.batch);
      } else if (response.data) {
        setCurrentBatch(response.data);
      } else {
        setCurrentBatch(batch);
      }
      setDetailVisible(true);
    } catch (error) {
      setCurrentBatch(batch);
      setDetailVisible(true);
    }
  };

  const handleDeleteBatch = async (id) => {
    try {
      const response = await adminAPI.deleteAdminBatch(id);
      if (response.code === 0) {
        message.success(t('delete_success'));
        fetchBatches(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || t('delete_failed'));
      }
    } catch (error) {
      message.error(t('delete_failed'));
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'reconcile': {color:'orange',text:localStorage.getItem('lang')==='en'?'Needs review':'待核对'},
      'cancelling': {color:'orange',text:localStorage.getItem('lang')==='en'?'Cancelling':'正在取消'},
      'pending': { color: 'gold', text: t('pending') || '待处理', icon: <ClockCircleOutlined /> },
      'in_progress': { color: 'blue', text: t('processing') || '处理中', icon: <SyncOutlined spin /> },
      'completed': { color: 'green', text: t('completed') || '已完成', icon: <CheckCircleOutlined /> },
      'failed': { color: 'red', text: t('failed') || '失败', icon: <CloseCircleOutlined /> },
      'cancelled': { color: 'default', text: t('cancelled') || '已取消', icon: <DeleteOutlined /> },
    };
    const config = statusMap[status] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    
    let date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      if (timestamp < 1e11) {
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(timestamp);
      }
    } else {
      return '-';
    }
    
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatEndpoint = (endpoint) => {
    if (!endpoint) return '-';
    return endpoint.replace('/v1/', '');
  };

  // 计算进度
  const getProgress = (batch) => {
    if (!batch.total_count || batch.total_count === 0) return 0;
    return Math.round(((batch.success_count || 0) + (batch.failed_count || 0)) / batch.total_count * 100);
  };

  // 统计数据
  const stats = {
    total: batches.length,
    pending: batches.filter(b => b.status === 'pending').length,
    in_progress: batches.filter(b => b.status === 'in_progress').length,
    completed: batches.filter(b => b.status === 'completed').length,
    failed: batches.filter(b => b.status === 'failed').length,
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => (
        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {id}
        </Text>
      ),
    },
    {
      title: t('batch_name') || '任务名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (name) => (
        <Text strong style={{ fontSize: 13 }}>{name || '批量任务'}</Text>
      ),
    },
    {
      title: t('batch_user') || t('user') || '用户',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
      render: (userId) => userId ? <Tag>{userId}</Tag> : '-',
    },
    {
      title: t('batch_status') || t('status') || '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => getStatusTag(status),
    },
    {
      title: t('progress') || '进度',
      key: 'progress',
      width: 160,
      render: (_, record) => {
        const progress = getProgress(record);
        const successRate = record.total_count > 0 
          ? ((record.success_count || 0) / record.total_count * 100).toFixed(1) 
          : '0';
        return (
          <div>
            <Progress 
              percent={progress} 
              size="small" 
              status={record.status === 'failed' ? 'exception' : record.status === 'completed' ? 'success' : 'active'}
              format={(p) => `${p}%`}
              style={{ marginBottom: 4 }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              成功 {record.success_count || 0} / 失败 {record.failed_count || 0} ({successRate}%)
            </Text>
          </div>
        );
      },
    },
    {
      title: t('model') || '模型',
      dataIndex: 'model',
      key: 'model',
      width: 130,
      render: (model) => model ? <Tag color="blue">{model}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: t('created_at') || '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time) => formatTime(time),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('detail')}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => showDetails(record)} />
          </Tooltip>
          <Popconfirm
            disabled={!['completed','failed','cancelled'].includes(record.status)}
            title={t('confirm_delete')}
            description={t('delete_warning')}
            onConfirm={() => handleDeleteBatch(record.id)}
            okText={t('confirm')}
            cancelText={t('cancel')}
          >
            <Tooltip title={t('delete')}>
              <Button size="small" danger disabled={!['completed','failed','cancelled'].includes(record.status)} icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        <ClockCircleOutlined style={{ marginRight: 8 }} />
        {t('batch_management')}
      </Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('total') || '总计'} value={stats.total} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('pending') || '待处理'} value={stats.pending} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('batch_processing') || '处理中'} value={stats.in_progress} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('completed') || '已完成'} value={stats.completed} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('failed') || '失败'} value={stats.failed} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button icon={<ReloadOutlined />} onClick={() => fetchBatches(pagination.current, pagination.pageSize)}>
            {t('batch_refresh') || t('refresh') || '刷新'}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={batches}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${t('total')}: ${total}`,
            onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
          }}
          size="small"
        />
      </Card>

      {/* 详情抽屉 - 重新设计 */}
      <Drawer
        title={
          <Space>
            <FileTextOutlined />
            <span>{t('batch_detail') || '任务详情'}</span>
            {currentBatch && getStatusTag(currentBatch.status)}
          </Space>
        }
        placement="right"
        width={600}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {currentBatch && (
          <div>
            {/* 进度概览 */}
            {currentBatch.total_count > 0 && (
              <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title="总任务数" 
                      value={currentBatch.total_count || 0} 
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="成功" 
                      value={currentBatch.success_count || 0} 
                      valueStyle={{ fontSize: 20, color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="失败" 
                      value={currentBatch.failed_count || 0} 
                      valueStyle={{ fontSize: 20, color: '#ff4d4f' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="完成率" 
                      value={`${getProgress(currentBatch)}%`} 
                      valueStyle={{ fontSize: 20, color: '#1890ff' }}
                    />
                  </Col>
                </Row>
                <Progress 
                  percent={getProgress(currentBatch)} 
                  status={currentBatch.status === 'failed' ? 'exception' : currentBatch.status === 'completed' ? 'success' : 'active'}
                  style={{ marginTop: 16 }}
                />
              </Card>
            )}

            {/* 基本信息 */}
            <Descriptions column={2} variant="bordered" size="small">
              <Descriptions.Item label="任务ID" span={2}>
                <Text copyable style={{ fontFamily: 'monospace', fontSize: 11 }}>
                  {currentBatch.id}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('batch_user') || t('user') || '用户'}>
                <Tag>{currentBatch.user_id || '-'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('batch_type') || t('type') || '任务类型'}>
                <Tag color="blue">{formatEndpoint(currentBatch.endpoint)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('model') || '模型'}>
                {currentBatch.model || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('input_file') || '输入文件'}>
                <Space>
                  <FileTextOutlined />
                  <Text>{currentBatch.file_name || currentBatch.input_file_id || '-'}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('output_file') || '输出文件'}>
                {currentBatch.output_file_id ? (
                  <Tooltip title="为保护用户隐私，管理端无法下载用户结果文件">
                    <Tag icon={<SafetyOutlined />} color="orange">已生成（隐私保护）</Tag>
                  </Tooltip>
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('created_at') || '创建时间'}>
                {formatTime(currentBatch.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label={t('completed_at') || '完成时间'}>
                {formatTime(currentBatch.completed_at)}
              </Descriptions.Item>
            </Descriptions>

            {/* 时间线 */}
            <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>执行时间线</Title>
            <Timeline
              items={[
                {
                  color: 'green',
                  children: (
                    <div>
                      <Text strong>任务创建</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatTime(currentBatch.created_at)}
                      </Text>
                    </div>
                  ),
                },
                ...(currentBatch.in_progress_at ? [{
                  color: 'blue',
                  children: (
                    <div>
                      <Text strong>开始处理</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatTime(currentBatch.in_progress_at)}
                      </Text>
                    </div>
                  ),
                }] : []),
                ...(currentBatch.status === 'completed' || currentBatch.status === 'failed' ? [{
                  color: currentBatch.status === 'completed' ? 'green' : 'red',
                  children: (
                    <div>
                      <Text strong>{currentBatch.status === 'completed' ? '任务完成' : '任务失败'}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatTime(currentBatch.completed_at)}
                      </Text>
                    </div>
                  ),
                }] : []),
              ]}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Batches;
